/**
 * PersonalRemindersDetailController — fiche détail d'un rappel personnel
 * §19.C.5 (EPIC-054 / 54.6).
 *
 * Charge `GET /me/personal-reminders/{id}` (rappel + template + 10
 * dernières complétions + total) et expose les actions :
 *  - `Marquer comme fait` → `POST .../mark-done` (visible si status=active)
 *  - `Activer`            → `POST .../activate`  (status=suggested)
 *  - `Réactiver`          → `POST .../reactivate`(status=dismissed)
 *  - `Désactiver`         → `POST .../dismiss`   (sauf mandatory=true)
 *  - `Annuler la réalisation` → `POST .../undo-completion`
 *    (visible si au moins une complétion effective non annulée).
 *
 * L'historique affiche jusqu'à 10 entrées (undos compris) avec un libellé
 * distinct pour les rows d'annulation. Un encart "Voir tout" est ajouté
 * si `completionsTotal > 10`.
 *
 * Convention `<template>` + `data-slot` (CLAUDE.md règle 8).
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

const STATE_LABELS = {
    suggested: "Suggéré",
    active: "Actif",
    completed: "Marqué comme fait",
    dismissed: "Désactivé",
};

const RECURRENCE_LABELS = {
    one_shot: "Unique",
    annual: "Annuelle",
    quarterly: "Trimestrielle",
    monthly: "Mensuelle",
};

const SOURCE_LABELS = {
    trigger_suggestion: "Suggéré automatiquement",
    auto_active_mandatory: "Activé automatiquement (obligatoire)",
    manual_activation: "Activé manuellement",
};

export default class PersonalRemindersDetailController extends BaseController {
    static targets = [
        "crumb", "title", "stateBadge", "mandatoryBadge",
        "description", "nextDueWrap", "nextDueValue", "ctaLink",
        "source", "recurrence", "activatedAt",
        "markDoneBtn", "activateBtn", "reactivateBtn", "dismissBtn", "undoBtn",
        "historyList", "historyEmpty", "historyMore", "historyCount", "historyTemplate",
        "error",
    ];

    connect() {
        const params = new URLSearchParams(window.location.search);
        this.reminderId = params.get("id") ?? "";
        if (this.reminderId === "") {
            this.showError("Identifiant de rappel manquant dans l'URL.");
            return;
        }
        this.load();
    }

    async load() {
        this.errorTarget.hidden = true;
        try {
            const response = await api.get(`/me/personal-reminders/${this.reminderId}`);
            const data = response?.data;
            if (!data?.reminder) {
                this.showError("Rappel introuvable.");
                return;
            }
            this.render(data);
        } catch {
            this.showError("Impossible de charger ce rappel. Réessayez plus tard.");
        }
    }

    render(data) {
        const r = data.reminder;
        const t = data.template;
        const title = t?.title ?? r.title ?? "Rappel";

        this.crumbTarget.textContent = title;
        this.titleTarget.textContent = title;
        document.title = `Notamicro — ${title}`;

        const stateLabel = STATE_LABELS[r.status];
        if (stateLabel) {
            this.stateBadgeTarget.textContent = stateLabel;
            this.stateBadgeTarget.className = "nm-badge nm-badge--" + this.badgeVariant(r.status);
            this.stateBadgeTarget.hidden = false;
        }
        if (r.mandatory) {
            this.mandatoryBadgeTarget.hidden = false;
        }

        this.descriptionTarget.textContent = t?.description ?? "";

        if (r.nextDueDate) {
            this.nextDueValueTarget.textContent = this.formatDate(r.nextDueDate);
            this.nextDueWrapTarget.hidden = false;
        }

        if (t?.ctaUrl) {
            this.ctaLinkTarget.href = t.ctaUrl;
            this.ctaLinkTarget.textContent = t.ctaLabel ?? "En savoir plus";
            this.ctaLinkTarget.hidden = false;
        }

        this.sourceTarget.textContent = SOURCE_LABELS[r.source] ?? r.source ?? "—";
        this.recurrenceTarget.textContent = t?.recurrence
            ? (RECURRENCE_LABELS[t.recurrence] ?? t.recurrence)
            : "—";
        this.activatedAtTarget.textContent = r.activatedAt
            ? this.formatDateTime(r.activatedAt)
            : "—";

        this.renderActions(r);
        this.renderHistory(data.completions ?? [], data.completionsTotal ?? 0);
    }

    renderActions(reminder) {
        const status = reminder.status;
        const mandatory = reminder.mandatory;

        const set = (target, visible) => { target.hidden = !visible; };

        set(this.markDoneBtnTarget, status === "active");
        set(this.activateBtnTarget, status === "suggested");
        set(this.reactivateBtnTarget, status === "dismissed");
        set(this.dismissBtnTarget, !mandatory && (status === "active" || status === "suggested"));

        const completedCount = this._effectiveCompletions ?? 0;
        set(this.undoBtnTarget, completedCount > 0);
    }

    renderHistory(completions, total) {
        this.historyListTarget.replaceChildren();

        if (completions.length === 0) {
            this.historyEmptyTarget.hidden = false;
            this.historyCountTarget.hidden = true;
            this.historyMoreTarget.hidden = true;
            this._effectiveCompletions = 0;
            return;
        }

        this.historyEmptyTarget.hidden = true;

        let effectiveBalance = 0;
        for (const c of completions) {
            const fragment = this.historyTemplateTarget.content.cloneNode(true);
            const dateEl = fragment.querySelector('[data-slot="date"]');
            const labelEl = fragment.querySelector('[data-slot="label"]');
            dateEl.textContent = this.formatDateTime(c.completedAt);
            labelEl.textContent = c.isUndo
                ? "Réalisation annulée"
                : (c.completedForPeriod
                    ? `Marqué comme fait pour la période ${this.formatDate(c.completedForPeriod)}`
                    : "Marqué comme fait");
            if (c.isUndo) effectiveBalance--;
            else effectiveBalance++;
            this.historyListTarget.appendChild(fragment);
        }
        this._effectiveCompletions = Math.max(0, effectiveBalance);

        this.historyCountTarget.textContent = `${total} entrée${total > 1 ? "s" : ""}`;
        this.historyCountTarget.hidden = false;
        this.historyMoreTarget.hidden = total <= 10;
    }

    async markDone() {
        await this.mutate("mark-done");
    }
    async activate() {
        await this.mutate("activate");
    }
    async reactivate() {
        await this.mutate("reactivate");
    }
    async dismiss() {
        await this.mutate("dismiss");
    }
    async undoCompletion() {
        await this.mutate("undo-completion");
    }

    async mutate(action) {
        const buttons = this.element.querySelectorAll("button");
        for (const btn of buttons) btn.disabled = true;
        try {
            await api.post(`/me/personal-reminders/${this.reminderId}/${action}`, {});
            await this.load();
        } catch {
            for (const btn of buttons) btn.disabled = false;
        }
    }

    showError(message) {
        this.errorTarget.textContent = message;
        this.errorTarget.hidden = false;
    }

    badgeVariant(state) {
        switch (state) {
            case "active": return "success";
            case "suggested": return "info";
            case "completed": return "muted";
            case "dismissed": return "muted";
            default: return "info";
        }
    }

    formatDate(isoDate) {
        try {
            const d = new Date(isoDate);
            return d.toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "long",
                year: "numeric",
            });
        } catch {
            return isoDate;
        }
    }

    formatDateTime(iso) {
        try {
            const d = new Date(iso);
            return d.toLocaleString("fr-FR", {
                day: "2-digit",
                month: "long",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch {
            return iso;
        }
    }
}
